import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const empty = { name: "", phase_number: "", planned_area: "", completed_area: 0, status: "Not Started", planned_start_date: "", planned_completion_date: "", actual_start_date: "", actual_completion_date: "", assigned_engineer: "", notes: "" };

function Field({ label, children, full }) {
  return <div className={`space-y-1.5 ${full ? "sm:col-span-2" : ""}`}><Label className="text-xs font-semibold text-slate-600">{label}</Label>{children}</div>;
}

export function PhaseFormDialog({ open, onOpenChange, projectId, phase, onSaved }) {
  const { meta } = useAuth();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const editing = !!phase;

  useEffect(() => {
    if (open) {
      setConfirmMsg("");
      setForm(phase ? { ...empty, ...phase, planned_area: phase.planned_area || "", completed_area: phase.completed_area || 0,
        planned_start_date: (phase.planned_start_date || "").slice(0, 10), planned_completion_date: (phase.planned_completion_date || "").slice(0, 10),
        actual_start_date: (phase.actual_start_date || "").slice(0, 10), actual_completion_date: (phase.actual_completion_date || "").slice(0, 10),
        assigned_engineer: phase.assigned_engineer || "" } : empty);
    }
  }, [open, phase]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (confirm = false) => {
    if (!form.name.trim()) { toast.error("Phase name is required"); return; }
    setSaving(true);
    const payload = { ...form, planned_area: Number(form.planned_area) || 0, completed_area: Number(form.completed_area) || 0,
      phase_number: form.phase_number ? Number(form.phase_number) : null, assigned_engineer: form.assigned_engineer || null, confirm };
    try {
      if (editing) await api.put(`/phases/${phase.id}`, payload);
      else await api.post(`/projects/${projectId}/phases`, payload);
      toast.success(editing ? "Phase updated" : "Phase added");
      onOpenChange(false); onSaved && onSaved();
    } catch (e) {
      if (e.response?.status === 409) { setConfirmMsg(apiError(e.response.data.detail)); }
      else toast.error(apiError(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const engineers = (meta?.users || []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl gap-0 p-0 max-h-[92vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-slate-100 px-6 py-4"><DialogTitle className="text-lg">{editing ? "Edit Phase" : "Add Execution Phase"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 overflow-y-auto px-6 py-5 sm:grid-cols-2">
          <Field label="Phase Name" full><Input data-testid="phase-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Phase 1 — Production Hall" /></Field>
          <Field label="Phase Number"><Input data-testid="phase-number" type="number" value={form.phase_number} onChange={(e) => set("phase_number", e.target.value)} placeholder="Auto" /></Field>
          <Field label="Status">
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger data-testid="phase-status"><SelectValue /></SelectTrigger>
              <SelectContent>{(meta?.phase_statuses || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Planned Area (Sq.Ft)"><Input data-testid="phase-planned-area" type="number" value={form.planned_area} onChange={(e) => set("planned_area", e.target.value)} /></Field>
          <Field label="Completed Area (Sq.Ft)"><Input data-testid="phase-completed-area" type="number" value={form.completed_area} onChange={(e) => set("completed_area", e.target.value)} /></Field>
          <Field label="Planned Start"><Input type="date" value={form.planned_start_date} onChange={(e) => set("planned_start_date", e.target.value)} /></Field>
          <Field label="Planned Completion"><Input type="date" value={form.planned_completion_date} onChange={(e) => set("planned_completion_date", e.target.value)} /></Field>
          <Field label="Actual Start"><Input type="date" value={form.actual_start_date} onChange={(e) => set("actual_start_date", e.target.value)} /></Field>
          <Field label="Actual Completion"><Input type="date" value={form.actual_completion_date} onChange={(e) => set("actual_completion_date", e.target.value)} /></Field>
          <Field label="Assigned Site Engineer" full>
            <Select value={form.assigned_engineer || ""} onValueChange={(v) => set("assigned_engineer", v)}>
              <SelectTrigger data-testid="phase-engineer"><SelectValue placeholder="Select engineer" /></SelectTrigger>
              <SelectContent>{engineers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Notes" full><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
          {confirmMsg && (
            <div className="sm:col-span-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /><span>{confirmMsg}</span>
            </div>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          {confirmMsg ? (
            <Button data-testid="phase-confirm" onClick={() => submit(true)} disabled={saving} className="bg-amber-600 font-semibold hover:bg-amber-700">Confirm & Save</Button>
          ) : (
            <Button data-testid="phase-submit" onClick={() => submit(false)} disabled={saving} className="font-semibold">{saving ? "Saving…" : editing ? "Save Phase" : "Add Phase"}</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
